import dayjs, { ManipulateType } from "dayjs";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import styled from "styled-components";
import { useApi } from "../../../../../hooks/useApi";
import useMy from "../../../../../hooks/useMy";
import { IBlackListInfo } from "../../../../../pages/Users/BlackList";
import Loader from "../../../../DefaultStyles/Loader";
import { OptionType } from "../../../../Filters/DropDown";
import { Textarea } from "../../../../Input/Textarea";
import DefaultModalLayout, {
  defaultModalType,
} from "../../../../Modal/DefaultModalLayout";
import { DefaultModalCommonProps } from "../CommentModal/CommentModal";
import TargetContainer, { ModalLabel } from "../CommentModal/TargetContainer";
import BlackListCheckRow from "./BlackListCheckRow";
import BlackListDateRow from "./BlackListDateRow";
import BlackListDeleteModal from "./BlackListDeleteModal";

export interface IBlackListCheck {
  create?: boolean;
  join?: boolean;
  withdraw?: boolean;
  chat?: boolean;
  withdrawCheck?: boolean;
}

interface BlackListModalProps extends DefaultModalCommonProps {
  blacklistData?: IBlackListInfo;
}

export default function BlackListModal({
  closeEvent,
  confirmEvent,
  type,
  blacklistData,
}: BlackListModalProps) {
  const initEndDate = () => {
    const startDate = `${dayjs(blacklistData?.startDay)?.format("YYYY-MM-DD")}`;

    const dayDiff = dayjs(startDate).diff(blacklistData?.endDay, "day");
    const monthDiff = dayjs(startDate).diff(blacklistData?.endDay, "month");
    const yearDiff = dayjs(startDate).diff(blacklistData?.endDay, "year");

    if (dayDiff === -7) {
      return "7_d";
    } else if (monthDiff === -1) {
      return "1_M";
    } else if (monthDiff === -6) {
      return "6_M";
    } else if (yearDiff === -1) {
      return "1_y";
    } else if (yearDiff === -10) {
      return "10_y";
    } else {
      return "";
    }
  };

  const { name, grade } = useMy();

  const [modalType, setModalType] = useRecoilState(defaultModalType);
  const [targetUid, setTargetUid] = useState(
    blacklistData
      ? `${blacklistData?.uid}_${blacklistData?.name}_${blacklistData?.nickname}`
      : ""
  );
  const [check, setCheck] = useState<IBlackListCheck>({
    create: blacklistData ? blacklistData?.create : false,
    join: blacklistData ? blacklistData?.join : false,
    withdraw: blacklistData ? blacklistData?.withdraw : false,
    chat: blacklistData ? blacklistData?.chat : false,
    withdrawCheck: blacklistData ? blacklistData?.comforming_withdraw : false,
  });
  const [startDate, setStartDate] = useState(
    blacklistData
      ? `${dayjs(blacklistData?.startDay)?.format("YYYY-MM-DD")}`
      : "시작일"
  );
  const [endDate, setEndDate] = useState<OptionType>({
    value: blacklistData ? initEndDate() : "",
    label: blacklistData
      ? `${dayjs(blacklistData?.endDay)?.format("YYYY-MM-DD")}`
      : "종료일",
  });
  const [memo, setMemo] = useState(blacklistData ? blacklistData?.reason : "");
  const [inputConfirmBtnClick, setInputConfirmBtnClick] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editConfirmBtnClick, setEditConfirmBtnClick] = useState(false);

  // 블랙리스트 마지막 수정 날짜
  const { response: editedResponse, loading: editedLoading } = useApi({
    method: "get",
    url:
      modalType === "view" && blacklistData
        ? `black-list/${blacklistData?.uid}`
        : undefined,
    data: {
      uid: Number(blacklistData?.uid),
    },
  });

  const blackListRequestData = {
    uid: Number(targetUid?.split("_")[0]),
    state:
      (check?.create ? 1 : 0) +
      (check?.join ? 2 : 0) +
      (check?.withdraw ? 4 : 0) +
      (check?.chat ? 8 : 0) +
      (check?.withdrawCheck ? 16 : 0),
    startd: startDate,
    endd: dayjs(startDate).add(
      Number(endDate?.value?.split("_")[0]),
      endDate?.value?.split("_")[1] as ManipulateType
    ),
    memo,
  };

  // 블랙리스트 추가
  const {
    response: inputResponse,
    loading: inputLoading,
    error: inputError,
  } = useApi({
    method: "post",
    url: inputConfirmBtnClick ? "black-list" : undefined,
    data: blackListRequestData,
  });

  // 블랙리스트 수정
  const {
    response: editResponse,
    loading: editLoading,
    error: editError,
  } = useApi({
    method: "post",
    url: editConfirmBtnClick ? `black-list/${blacklistData?.uid}` : undefined,
    data: blackListRequestData,
  });

  const confirmBtnDisabled = () => {
    if (modalType === "input") {
      if (
        memo === "" ||
        startDate === "" ||
        endDate?.value === "" ||
        targetUid === "" ||
        Object.values(check).filter(Boolean).length === 0
      ) {
        return true;
      } else {
        return false;
      }
    } else if (modalType === "edit") {
      if (
        blacklistData?.reason === memo &&
        dayjs(blacklistData?.startDay)?.format("YYYY-MM-DD") === startDate &&
        dayjs(blacklistData?.endDay)?.format("YYYY-MM-DD") === endDate?.label &&
        blacklistData?.create === check?.create &&
        blacklistData?.join === check?.join &&
        blacklistData?.withdraw === check?.withdraw &&
        blacklistData?.chat === check?.chat
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  useEffect(() => {
    if (editResponse?.status === 200) {
      closeEvent();

      if (confirmEvent) {
        confirmEvent();
      }

      toast.success("블랙리스트가 수정되었습니다.", {});
    }
  }, [closeEvent, editResponse, confirmEvent]);

  useEffect(() => {
    if (inputResponse?.status === 201) {
      closeEvent();

      if (confirmEvent) {
        confirmEvent();
      }

      toast.success("블랙리스트가 추가되었습니다.", {});
    }
  }, [closeEvent, confirmEvent, inputResponse]);

  useEffect(() => {
    if (inputError) {
      toast.error(inputError.data);
    } else if (editError) {
      toast.error(editError.data);
    }
  }, [inputError, editError]);

  useEffect(() => {
    setModalType(type);
  }, [type]);

  useEffect(() => {}, [startDate]);

  return (
    <DefaultModalLayout
      type={type}
      modalTitle={
        modalType === "view"
          ? "블랙리스트 보기"
          : modalType === "edit"
          ? "블랙리스트 수정"
          : "블랙리스트 추가"
      }
      confirmEvent={() => {
        if (modalType === "input") {
          setInputConfirmBtnClick(true);
        } else if (modalType === "edit") {
          setEditConfirmBtnClick(true);
        }
      }}
      closeEvent={() => closeEvent()}
      cancelBtnDisabled={
        modalType === "input"
          ? false
          : grade === 1 || blacklistData?.writer === name
          ? false
          : true
      }
      deleteBtnShow={
        (grade === 1 || blacklistData?.writer === name) && modalType === "view"
      }
      deleteBtnClickEvent={() => {
        setDeleteModal(true);
      }}
      confirmDisabled={confirmBtnDisabled()}
      leftBottomTitle={
        modalType === "view" ? (blacklistData ? "작성자" : "관리자") : "관리자"
      }
      leftBottomName={
        modalType === "view" ? blacklistData?.writer ?? undefined : undefined
      }
      leftBottomTime={
        editedResponse?.status === 200 && modalType === "view"
          ? editedResponse?.data?.update
          : undefined
      }
    >
      {/* 대상 */}
      <ModalRow>
        <TargetContainer
          targetUid={targetUid}
          setTargetUid={setTargetUid}
          modalType={type}
        />
      </ModalRow>

      {/* 제한 항목 */}
      <BlackListCheckRow
        modalType={modalType}
        check={check}
        setCheck={setCheck}
      />

      {/* 기간 */}
      <BlackListDateRow
        modalType={modalType}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />

      {/* 내용 */}
      <ModalLabel>내용</ModalLabel>

      <Textarea
        placeholder="내용 입력 (필수)"
        required={true}
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
        readOnly={modalType === "view"}
        maxLength={140}
      />

      {deleteModal ? (
        <BlackListDeleteModal
          cancelEvent={() => setDeleteModal(false)}
          userId={Number(blacklistData?.uid)}
          closeEvent={() => {
            closeEvent();

            if (confirmEvent) {
              confirmEvent();
            }
          }}
        />
      ) : (
        <></>
      )}

      <>{(editedLoading || inputLoading || editLoading) && <Loader />}</>
    </DefaultModalLayout>
  );
}

export const ModalRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;
